// GraphScreen.jsx — Two-panel graph: DAG (top, 360px) + Force-directed (bottom, flex:1)
// DAG ported from legacy renderDAGGraph using pure topological layer algorithm.
// FDG preserved from original port of renderSubjectGraph.
import React from 'react';
import * as d3 from 'd3';
import { api } from '../api';
import { CoursePills, TINT } from '../components/CoursePills';

// ── Design-token color helpers ──────────────────────────────────────────────────

// Maps a node's mastery/retrievability/frontier status to Didactic fill+stroke.
// Status→color rule:
//   frontier (isFrontier or dist===0) → transparent fill, 1px dashed var(--probe) stroke
//   proven   (mastery≥0.3, R≥0.7)    → var(--ok) fill
//   decaying (mastery≥0.3, R<0.7)    → var(--tint-amber) fill
//   failed   (status==='failed')      → var(--tint-red) fill
//   locked   (mastery===0, not front) → var(--paper-4) fill, ink-3 stroke
//   anchor-unproven                   → #fff fill, 1.5px solid var(--ink-2) border
function nodeStatusColor(node, frontierDist) {
  const m = node.mastery || 0;
  const R = node.retrievability != null ? node.retrievability : 0;
  const hasSigs = m >= 0.3;

  // Frontier: computed BFS distance === 0, or flagged via API
  const dist = (frontierDist && frontierDist[node.id] != null) ? frontierDist[node.id] : Infinity;
  const isFront = node.isFrontier || dist === 0;

  if (isFront) {
    return { fill: 'transparent', stroke: 'rgb(151,71,255)', strokeDash: '4 3', strokeW: 1.5 };
  }
  if (hasSigs && R >= 0.7) {
    return { fill: 'rgb(97,197,84)', stroke: 'rgb(60,150,50)', strokeDash: '0', strokeW: 1.5 };
  }
  if (hasSigs && R < 0.7) {
    return { fill: 'rgb(245,191,79)', stroke: 'rgb(180,130,10)', strokeDash: '0', strokeW: 1.5 };
  }
  if (node.status === 'failed') {
    return { fill: 'rgb(249,111,98)', stroke: 'rgb(200,60,40)', strokeDash: '0', strokeW: 1.5 };
  }
  if (!hasSigs && dist === -1) {
    // Practiced but R near 0 — shouldn't normally happen, treat as decaying
    return { fill: 'rgb(245,191,79)', stroke: 'rgb(180,130,10)', strokeDash: '0', strokeW: 1 };
  }
  // Locked (far from frontier) — opacity dims with dist in render, but color stays khaki
  if (dist === Infinity || dist > 0) {
    return { fill: 'rgb(205,191,177)', stroke: 'rgb(159,159,159)', strokeDash: '0', strokeW: 1 };
  }
  // Anchor unproven (root with no mastery)
  return { fill: '#fff', stroke: 'rgb(86,64,41)', strokeDash: '0', strokeW: 1.5 };
}

function edgeColor(edge) {
  if (edge.isFrontier) {
    return { stroke: 'rgb(151,71,255)', dash: '5 3', width: 1 };
  }
  return { stroke: 'rgba(173,89,5,0.4)', dash: '0', width: 1 };
}

// Map status → chip class for the side panel
function statusToChip(node) {
  const m = node.mastery || 0;
  const R = node.retrievability != null ? node.retrievability : 0;
  if (node.isFrontier) return 'frontier';
  if (node.status === 'failed') return 'decay';
  if (m >= 0.3 && R >= 0.7) return 'crisp';
  if (m >= 0.3 && R < 0.7) return 'decay';
  if (m === 0) return 'blank';
  return 'blurry';
}

function statusLabel(chip) {
  return { crisp: 'Proven', frontier: 'Frontier', decay: 'Decaying', blurry: 'Blurry', blank: 'Locked' }[chip] || chip;
}

// ── Parse API graph into flat nodes + links ───────────────────────────────────
// Ported from legacy renderSubjectGraph (L1842–1916) + renderDAGGraph (L2666–2703).
// Key addition: stub nodes created on the fly for dangling edge.from references.

function parseGraphData(data) {
  const nodes = [];
  const links = [];
  const nodeMap = {};
  const frontierSet = new Set(data.frontier || []);
  const optimalSet = new Set((data.optimal_review_set || []).map(x => x.id));

  const domains = data.domains || {};
  for (const [domainName, domain] of Object.entries(domains)) {
    const subjects = domain.subjects || {};
    for (const [subjectName, subject] of Object.entries(subjects)) {
      // Anchor nodes
      for (const a of (subject.anchors || [])) {
        if (nodeMap[a.id]) continue;
        const node = {
          id: a.id,
          name: a.name || a.id,
          type: 'anchor',
          mastery: a.mastery || 0,
          retrievability: a.retrievability,
          stability: a.stability,
          difficulty: a.difficulty,
          statement: a.statement || '',
          subject: subjectName,
          domain: domainName,
          isFrontier: frontierSet.has(a.id),
          isOptimal: optimalSet.has(a.id),
          status: a.status || 'anchor',
        };
        nodes.push(node);
        nodeMap[a.id] = node;
      }

      // Edges → derived nodes + links
      for (const edge of (subject.edges || [])) {
        const targetId = edge.to;
        if (!nodeMap[targetId]) {
          const gen = (edge.generates_anchor || [])[0];
          const node = {
            id: targetId,
            name: gen?.name || (targetId + '').replace(/_/g, ' '),
            type: 'derived',
            mastery: edge.mastery || 0,
            retrievability: edge.retrievability,
            stability: edge.stability,
            difficulty: edge.difficulty,
            statement: gen?.statement || edge.statement || '',
            subject: subjectName,
            domain: domainName,
            isFrontier: frontierSet.has(edge.id) || frontierSet.has(targetId),
            isOptimal: optimalSet.has(edge.id),
            status: edge.status || 'blank',
            edgeId: edge.id,
          };
          nodes.push(node);
          nodeMap[targetId] = node;
        } else {
          // Update existing node with richer edge data
          const n = nodeMap[targetId];
          n.status = edge.status || n.status;
          n.mastery = edge.mastery != null ? edge.mastery : n.mastery;
          n.retrievability = edge.retrievability != null ? edge.retrievability : n.retrievability;
          n.stability = edge.stability != null ? edge.stability : n.stability;
          n.difficulty = edge.difficulty != null ? edge.difficulty : n.difficulty;
          n.edgeId = edge.id;
          n.isFrontier = n.isFrontier || frontierSet.has(edge.id);
          n.isOptimal = n.isOptimal || optimalSet.has(edge.id);
        }

        // Prerequisite links — stub any from-ids not yet in nodeMap
        for (const fromId of (edge.from || [])) {
          if (!nodeMap[fromId]) {
            // Dangling prereq (cross-subject or not yet exported): create a stub node
            const stub = {
              id: fromId,
              name: (fromId + '').replace(/_/g, ' '),
              type: 'anchor',
              mastery: 0, retrievability: 0, stability: 1, difficulty: 0.5,
              statement: '', subject: subjectName, domain: domainName,
              isFrontier: false, isOptimal: false, status: 'blank',
            };
            nodes.push(stub);
            nodeMap[fromId] = stub;
          }
          links.push({
            source: fromId,
            target: targetId,
            status: edge.status,
            isFrontier: frontierSet.has(edge.id),
            edgeId: edge.id,
          });
        }
      }
    }
  }

  // Final safety: drop any link whose endpoints still aren't in nodeMap.
  // This prevents d3.forceLink from throwing "node not found: <id>".
  const validLinks = links.filter(l => nodeMap[l.source] && nodeMap[l.target]);
  return { nodes, links: validLinks, nodeMap };
}

// ── Frontier BFS (ported from legacy L1959–2004 / L2761–2808) ─────────────────
// Returns a map: nodeId → distance (-1 = practiced, 0 = frontier, N = locked N hops out)

function computeFrontierDist(nodes, links) {
  const practiced = {};
  for (const n of nodes) practiced[n.id] = (n.mastery || 0) >= 0.3;

  // Build parent adjacency from links
  const parents = {}; // childId → [parentId]
  for (const l of links) {
    const src = typeof l.source === 'object' ? l.source.id : l.source;
    const tgt = typeof l.target === 'object' ? l.target.id : l.target;
    if (!parents[tgt]) parents[tgt] = [];
    parents[tgt].push(src);
  }

  // Build children adjacency for BFS forward pass
  const children = {};
  for (const [cid, pids] of Object.entries(parents)) {
    for (const pid of pids) {
      if (!children[pid]) children[pid] = [];
      children[pid].push(cid);
    }
  }

  const dist = {};
  const queue = [];

  for (const n of nodes) {
    if (practiced[n.id]) {
      dist[n.id] = -1;
      continue;
    }
    const pids = parents[n.id] || [];
    const allParentsPracticed = pids.length === 0 || pids.every(pid => practiced[pid]);
    if (allParentsPracticed) {
      dist[n.id] = 0;
      queue.push(n.id);
    } else {
      dist[n.id] = Infinity;
    }
  }

  let qi = 0;
  while (qi < queue.length) {
    const curr = queue[qi++];
    for (const childId of (children[curr] || [])) {
      if (dist[childId] > dist[curr] + 1) {
        dist[childId] = dist[curr] + 1;
        queue.push(childId);
      }
    }
  }

  return dist;
}

// ── Node radius scaled by connectivity degree (min 7, max 14) ─────────────────
function computeNodeRadius(node, degreeMap) {
  const deg = degreeMap[node.id] || 0;
  const maxDeg = 10; // cap for scaling
  return 7 + Math.min(deg / maxDeg, 1) * 7;
}

// ── DAG layout: topological layer assignment ───────────────────────────────────
// computeLayers(nodes, links) → Map<nodeId, layerIndex>
// Uses longest-path-from-root algorithm:
//   layer = max(parent layers) + 1, roots at layer 0.
// This replicates Sugiyama's "layer assignment" step without d3-dag.

function computeLayers(nodes, links) {
  // Build parent map: nodeId → Set<parentId>
  const parents = {};
  const children = {};
  for (const n of nodes) { parents[n.id] = new Set(); children[n.id] = new Set(); }
  for (const l of links) {
    const src = typeof l.source === 'object' ? l.source.id : l.source;
    const tgt = typeof l.target === 'object' ? l.target.id : l.target;
    if (parents[tgt]) parents[tgt].add(src);
    if (children[src]) children[src].add(tgt);
  }

  const layer = {};
  // Kahn's algorithm for topological order + layer assignment
  const inDeg = {};
  for (const n of nodes) inDeg[n.id] = parents[n.id].size;

  const queue = nodes.filter(n => inDeg[n.id] === 0).map(n => n.id);
  for (const id of queue) layer[id] = 0;

  let qi = 0;
  while (qi < queue.length) {
    const curr = queue[qi++];
    const currLayer = layer[curr] || 0;
    for (const childId of (children[curr] || [])) {
      const proposed = currLayer + 1;
      if (proposed > (layer[childId] || 0)) layer[childId] = proposed;
      inDeg[childId] = (inDeg[childId] || 1) - 1;
      if (inDeg[childId] <= 0) queue.push(childId);
    }
  }

  // Any node not yet assigned (cycle residue) gets layer = max + 1
  let maxL = 0;
  for (const id of Object.keys(layer)) if (layer[id] > maxL) maxL = layer[id];
  for (const n of nodes) if (layer[n.id] == null) layer[n.id] = maxL + 1;

  return layer;
}

// dagNodeVisual(node, frontierDist) — same design tokens as FDG nodeStatusColor
// but returns { fill, stroke, strokeDash, strokeW, opacity } for rect nodes.
function dagNodeVisual(node, frontierDist) {
  const col = nodeStatusColor(node, frontierDist);
  const dist = (frontierDist && frontierDist[node.id] != null) ? frontierDist[node.id] : Infinity;
  let opacity = 1;
  if (dist > 0 && dist !== Infinity) opacity = Math.max(0.3, 0.95 - dist * 0.2);
  if (dist === Infinity) opacity = 0.28;
  if (dist === -1 || dist === 0) opacity = 1;
  return { ...col, opacity };
}

// ── Legend rows ────────────────────────────────────────────────────────────────

const LEGEND_ITEMS = [
  { label: 'Proven',   fill: 'rgb(97,197,84)',   stroke: 'rgb(60,150,50)',    dash: false },
  { label: 'Frontier', fill: 'transparent',      stroke: 'rgb(151,71,255)',   dash: true  },
  { label: 'Decaying', fill: 'rgb(245,191,79)',  stroke: 'rgb(180,130,10)',   dash: false },
  { label: 'Failed',   fill: 'rgb(249,111,98)',  stroke: 'rgb(200,60,40)',    dash: false },
  { label: 'Locked',   fill: 'rgb(205,191,177)', stroke: 'rgb(159,159,159)', dash: false },
];

// ── Main component ─────────────────────────────────────────────────────────────

export function GraphScreen() {
  const svgRef = React.useRef(null);
  const simRef = React.useRef(null);
  const dagSvgRef = React.useRef(null);
  const [graphData, setGraphData] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  // "dag" (default, layered derivation path) or "map" (force-directed).
  // Persisted so the user's choice sticks across tab switches / reloads.
  const [viewMode, setViewMode] = React.useState(
    () => localStorage.getItem("didactic.graphView") || "dag"
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [dagError, setDagError] = React.useState(null);

  // Course filter state
  const [planCourses, setPlanCourses] = React.useState([]);
  const [courseSelected, setCourseSelected] = React.useState(() => new Set());
  // courseNodeMap: { courseId -> Set<nodeId> }
  const [courseNodeMap, setCourseNodeMap] = React.useState(null);

  // ── Fetch graph data ──────────────────────────────────────────────────────
  React.useEffect(() => {
    if (api.getGraph) {
      api.getGraph()
        .then(d => { setGraphData(d); setLoading(false); })
        .catch(e => { setError(e.message); setLoading(false); });
    } else {
      setError('API not available');
      setLoading(false);
    }
  }, []);

  // ── Fetch plan courses + course→node mapping ──────────────────────────────
  React.useEffect(() => {
    api.getPlan()
      .then(p => {
        const courses = p.courses || [];
        setPlanCourses(courses);
        setCourseSelected(new Set(courses.map(c => c.id)));
      })
      .catch(() => {}); // no plan data — pills stay hidden

    api.getCourses()
      .then(r => {
        const raw = r.courses || {};
        const map = {};
        for (const [cid, cdata] of Object.entries(raw)) {
          map[cid] = new Set(cdata.nodes || []);
        }
        setCourseNodeMap(map);
      })
      .catch(() => {});
  }, []);

  const toggleCourse = (id) => {
    setCourseSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (next.size === 0) next.add(id); // never allow empty selection
      return next;
    });
  };

  // ── Derive filtered graph data ────────────────────────────────────────────
  const filteredGraphData = React.useMemo(() => {
    if (!graphData) return null;
    if (!courseNodeMap || planCourses.length === 0) return graphData;

    const allowedNodes = new Set();
    for (const cid of courseSelected) {
      const nodeSet = courseNodeMap[cid];
      if (nodeSet) nodeSet.forEach(n => allowedNodes.add(n));
    }
    if (allowedNodes.size === 0) return graphData; // safety fallback

    const filtered = { ...graphData, domains: {} };
    for (const [dn, domain] of Object.entries(graphData.domains || {})) {
      const filteredSubjects = {};
      for (const [sn, subject] of Object.entries(domain.subjects || {})) {
        const anchors = (subject.anchors || []).filter(a => allowedNodes.has(a.id));
        const edges = (subject.edges || []).filter(e =>
          allowedNodes.has(e.to) || (e.from || []).some(f => allowedNodes.has(f))
        );
        if (anchors.length > 0 || edges.length > 0) {
          filteredSubjects[sn] = { ...subject, anchors, edges };
        }
      }
      if (Object.keys(filteredSubjects).length > 0) {
        filtered.domains[dn] = { ...domain, subjects: filteredSubjects };
      }
    }
    return filtered;
  }, [graphData, courseSelected, courseNodeMap, planCourses.length]);

  // ── Build and run D3 simulation ───────────────────────────────────────────
  // Core loop ported from legacy renderSubjectGraph (L1842–2109):
  //   – parseGraphData for nodes/links
  //   – computeFrontierDist (BFS, L1959–2004)
  //   – forceSimulation with mastery-encoded spring distances (L1931–1954)
  //   – drag, zoom, click-to-select, hover-to-label (L2057–2098)
  React.useEffect(() => {
    if (!filteredGraphData || !svgRef.current) return;
    if (!d3) { setError('D3 not loaded'); return; }

    let cleanup = () => {};
    try {
      const svgEl = svgRef.current;
      const { nodes, links, nodeMap } = parseGraphData(filteredGraphData);

      if (nodes.length === 0) {
        // Nothing to render — clear and bail cleanly
        if (simRef.current) { simRef.current.stop(); simRef.current = null; }
        d3.select(svgEl).selectAll('*').remove();
        return;
      }

      // Dimensions from container
      const rect = svgEl.getBoundingClientRect();
      const W = rect.width || 800;
      const H = rect.height || 600;

      // Stop any existing simulation before rebuilding
      if (simRef.current) { simRef.current.stop(); simRef.current = null; }

      // Clear previous SVG contents for clean rebuild (tab switching safety)
      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      // ── Compute frontier BFS distances ──────────────────────────────────
      const frontierDist = computeFrontierDist(nodes, links);

      // ── Compute degree for radius scaling ───────────────────────────────
      const degreeMap = {};
      for (const l of links) {
        const src = typeof l.source === 'object' ? l.source.id : l.source;
        const tgt = typeof l.target === 'object' ? l.target.id : l.target;
        degreeMap[src] = (degreeMap[src] || 0) + 1;
        degreeMap[tgt] = (degreeMap[tgt] || 0) + 1;
      }

      // ── Arrow markers (burnt sienna + probe purple) ─────────────────────
      const defs = svg.append('defs');
      defs.append('marker')
        .attr('id', 'arrow-main')
        .attr('viewBox', '0 0 10 10')
        .attr('refX', 18).attr('refY', 5)
        .attr('markerWidth', 6).attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,0 L10,5 L0,10 Z')
        .attr('fill', 'rgba(173,89,5,0.5)');

      defs.append('marker')
        .attr('id', 'arrow-frontier')
        .attr('viewBox', '0 0 10 10')
        .attr('refX', 18).attr('refY', 5)
        .attr('markerWidth', 6).attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,0 L10,5 L0,10 Z')
        .attr('fill', 'rgb(151,71,255)');

      // ── Root group (zoom target) ────────────────────────────────────────
      const g = svg.append('g').attr('class', 'graph-root');

      // ── Zoom + pan (legacy L300–303) ────────────────────────────────────
      const zoom = d3.zoom()
        .scaleExtent([0.1, 5])
        .on('zoom', (e) => g.attr('transform', e.transform));
      svg.call(zoom);

      // ── Scatter initial positions near center ───────────────────────────
      // (prevents cold-start top-left clustering, legacy L1921–1924)
      for (const n of nodes) {
        n.x = W / 2 + (Math.random() - 0.5) * 200;
        n.y = H / 2 + (Math.random() - 0.5) * 200;
      }

      // ── Force simulation ────────────────────────────────────────────────
      // Spring distances encode learning state (legacy L1927–1954):
      //   mastered pair → tight (30px, strength 0.85)
      //   unknown pair  → loose (180px, strength 0.05)
      const sim = d3.forceSimulation(nodes)
        .force('link', d3.forceLink(links).id(d => d.id)
          .distance(d => {
            const srcM = (typeof d.source === 'object' ? d.source.mastery : 0) || 0;
            const tgtM = (typeof d.target === 'object' ? d.target.mastery : 0) || 0;
            const pairM = Math.min(srcM, tgtM);
            return 30 + (1 - pairM) * 150;
          })
          .strength(d => {
            const srcM = (typeof d.source === 'object' ? d.source.mastery : 0) || 0;
            const tgtM = (typeof d.target === 'object' ? d.target.mastery : 0) || 0;
            const pairM = Math.min(srcM, tgtM);
            return 0.05 + pairM * 0.8;
          }))
        .force('charge', d3.forceManyBody().strength(-220))
        .force('collide', d3.forceCollide().radius(22))
        .force('x', d3.forceX(W / 2).strength(0.03))
        .force('y', d3.forceY(H / 2).strength(0.03));

      simRef.current = sim;

      // ── Locked node opacity drops with BFS distance ─────────────────────
      function nodeOpacity(d) {
        const dist = frontierDist[d.id];
        if (dist == null || dist === -1 || dist === 0) return 1;
        if (dist === Infinity) return 0.3;
        return Math.max(0.3, 0.95 - dist * 0.2);
      }

      // ── Draw edges ──────────────────────────────────────────────────────
      const linkEls = g.selectAll('.g-link').data(links).enter().append('line')
        .attr('class', 'g-link')
        .attr('stroke', d => edgeColor(d).stroke)
        .attr('stroke-width', d => edgeColor(d).width)
        .attr('stroke-dasharray', d => edgeColor(d).dash)
        .attr('marker-end', d => d.isFrontier ? 'url(#arrow-frontier)' : 'url(#arrow-main)');

      // ── Draw node groups ────────────────────────────────────────────────
      const nodeG = g.selectAll('.g-node').data(nodes).enter().append('g')
        .attr('class', 'g-node')
        .style('cursor', 'pointer')
        .style('opacity', d => nodeOpacity(d))
        // Hover: show label + restore opacity
        .on('mouseover', function(e, d) {
          d3.select(this).style('opacity', 1);
          d3.select(this).select('.node-label').style('display', 'block');
        })
        .on('mouseout', function(e, d) {
          d3.select(this).style('opacity', nodeOpacity(d));
          d3.select(this).select('.node-label').style('display', 'none');
        })
        // Click: select node, show right panel (legacy L2065–2068)
        .on('click', function(e, d) {
          e.stopPropagation();
          // Use functional updater so we always get fresh state reference
          setSelected(prev => (prev && prev.id === d.id) ? null : d);
        })
        // Drag (legacy L2069–2073)
        .call(d3.drag()
          .on('start', (e, d) => {
            if (!e.active) sim.alphaTarget(0.1).restart();
            d.fx = d.x; d.fy = d.y;
          })
          .on('drag', (e, d) => { d.fx = e.x; d.fy = e.y; })
          .on('end', (e, d) => {
            if (!e.active) sim.alphaTarget(0);
            d.fx = null; d.fy = null;
          })
        );

      // Node circles — radius by degree, fill/stroke by status
      nodeG.append('circle')
        .attr('class', 'node-circle')
        .attr('r', d => computeNodeRadius(d, degreeMap))
        .attr('fill', d => nodeStatusColor(d, frontierDist).fill)
        .attr('stroke', d => nodeStatusColor(d, frontierDist).stroke)
        .attr('stroke-width', d => nodeStatusColor(d, frontierDist).strokeW)
        .attr('stroke-dasharray', d => nodeStatusColor(d, frontierDist).strokeDash);

      // Labels: shown on hover only (legacy: no persistent labels)
      nodeG.append('text')
        .attr('class', 'node-label')
        .attr('y', d => computeNodeRadius(d, degreeMap) + 13)
        .attr('text-anchor', 'middle')
        .attr('font-family', 'Host Grotesk, Inter, sans-serif')
        .attr('font-size', '11')
        .attr('fill', 'rgb(0,0,0)')
        .attr('letter-spacing', '-0.03em')
        .attr('pointer-events', 'none')
        .style('display', 'none')
        .text(d => d.name.length > 26 ? d.name.slice(0, 24) + '…' : d.name);

      // ── Tick function ───────────────────────────────────────────────────
      sim.on('tick', () => {
        linkEls
          .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x).attr('y2', d => d.target.y);
        nodeG.attr('transform', d => `translate(${d.x},${d.y})`);
      });

      // Background click → deselect
      svg.on('click', () => setSelected(null));

      cleanup = () => {
        sim.stop();
        simRef.current = null;
      };
    } catch (err) {
      console.error('GraphScreen D3 build failed:', err);
      setError('Graph render failed: ' + (err && err.message ? err.message : String(err)));
    }

    return cleanup;
  }, [filteredGraphData]);

  // ── Build DAG (topological layered layout) ────────────────────────────────
  // Ported from legacy renderDAGGraph (L2643–2955).
  // Layout: pure topological longest-path layers (computeLayers), no d3-dag library.
  // Nodes are 200×52 rounded rects, top-to-bottom flow.
  // Edges: d3.line with curveBumpY, colored by target node status.
  React.useEffect(() => {
    if (!filteredGraphData || !dagSvgRef.current) return;
    if (!d3) return;

    setDagError(null);
    try {
      const svgEl = dagSvgRef.current;
      const { nodes, links, nodeMap } = parseGraphData(filteredGraphData);

      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      if (nodes.length === 0) return;

      // ── Layer assignment ──────────────────────────────────────────────────
      const layerMap = computeLayers(nodes, links);
      const maxLayer = Math.max(0, ...Object.values(layerMap));

      // Group nodes by layer
      const byLayer = {};
      for (const n of nodes) {
        const l = layerMap[n.id] || 0;
        if (!byLayer[l]) byLayer[l] = [];
        byLayer[l].push(n);
      }

      // ── Frontier BFS distances ────────────────────────────────────────────
      const frontierDist = computeFrontierDist(nodes, links);

      // ── Coordinate placement ──────────────────────────────────────────────
      const nodeW = 200;
      const nodeH = 52;
      const layerGapY = 88;   // vertical gap between layers (top-to-bottom flow)
      const nodeGapX = 24;    // horizontal gap between nodes in same layer

      const rect = svgEl.getBoundingClientRect();
      const W = rect.width || 800;
      const H = rect.height || 360;

      // Assign x/y positions per layer
      const posMap = {}; // nodeId → {x, y}
      for (let li = 0; li <= maxLayer; li++) {
        const layerNodes = byLayer[li] || [];
        const rowW = layerNodes.length * nodeW + (layerNodes.length - 1) * nodeGapX;
        const startX = (W - rowW) / 2;
        const y = li * layerGapY + 20;
        layerNodes.forEach((n, i) => {
          posMap[n.id] = { x: startX + i * (nodeW + nodeGapX), y };
        });
      }

      // Total DAG size for scaling
      const dagW = Math.max(...nodes.map(n => (posMap[n.id]?.x || 0) + nodeW)) + 20;
      const dagH = (maxLayer + 1) * layerGapY + nodeH + 20;
      const scale = Math.min(W / dagW, H / dagH, 1.0) * 0.92;
      const offsetX = (W - dagW * scale) / 2;
      const offsetY = (H - dagH * scale) / 2;

      // ── SVG setup ─────────────────────────────────────────────────────────
      const defs = svg.append('defs');
      defs.append('marker')
        .attr('id', 'dag-arrow-r').attr('viewBox', '0 0 10 10')
        .attr('refX', 9).attr('refY', 5)
        .attr('markerWidth', 6).attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path').attr('d', 'M0,1 L9,5 L0,9 Z').attr('fill', 'rgba(173,89,5,0.55)');

      defs.append('marker')
        .attr('id', 'dag-arrow-r-front').attr('viewBox', '0 0 10 10')
        .attr('refX', 9).attr('refY', 5)
        .attr('markerWidth', 6).attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path').attr('d', 'M0,1 L9,5 L0,9 Z').attr('fill', 'rgb(151,71,255)');

      const g = svg.append('g')
        .attr('transform', `translate(${offsetX},${offsetY}) scale(${scale})`);

      svg.call(d3.zoom().scaleExtent([0.15, 4])
        .on('zoom', e => g.attr('transform', e.transform)));

      // ── Draw edges ────────────────────────────────────────────────────────
      const lineGen = d3.line().curve(d3.curveBumpY);

      for (const l of links) {
        const srcId = typeof l.source === 'object' ? l.source.id : l.source;
        const tgtId = typeof l.target === 'object' ? l.target.id : l.target;
        const sp = posMap[srcId];
        const tp = posMap[tgtId];
        if (!sp || !tp) continue;

        const tgtNode = nodeMap[tgtId];
        const vis = tgtNode ? dagNodeVisual(tgtNode, frontierDist) : null;
        const edgeStroke = l.isFrontier ? 'rgb(151,71,255)' : (vis ? vis.stroke : 'rgba(173,89,5,0.4)');
        const edgeDash = l.isFrontier ? '5 3' : '0';
        const edgeOpacity = vis ? Math.min(1, vis.opacity + 0.2) : 0.5;
        const markerEnd = l.isFrontier ? 'url(#dag-arrow-r-front)' : 'url(#dag-arrow-r)';

        // Points: bottom-center of source → top-center of target
        const pts = [
          [sp.x + nodeW / 2, sp.y + nodeH],
          [tp.x + nodeW / 2, tp.y],
        ];

        g.append('path')
          .attr('d', lineGen(pts))
          .attr('fill', 'none')
          .attr('stroke', edgeStroke)
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', edgeDash)
          .attr('stroke-linecap', 'round')
          .style('opacity', edgeOpacity)
          .attr('marker-end', markerEnd);
      }

      // ── Draw node groups ──────────────────────────────────────────────────
      for (const n of nodes) {
        const pos = posMap[n.id];
        if (!pos) continue;
        const vis = dagNodeVisual(n, frontierDist);

        const ng = g.append('g')
          .attr('transform', `translate(${pos.x}, ${pos.y})`)
          .style('cursor', 'pointer')
          .style('opacity', vis.opacity)
          .on('mouseover', function() {
            d3.select(this).style('opacity', 1);
            d3.select(this).select('.dag-label').style('opacity', 1);
          })
          .on('mouseout', function() {
            d3.select(this).style('opacity', vis.opacity);
            d3.select(this).select('.dag-label').style('opacity', 0.85);
          })
          .on('click', function(e) {
            e.stopPropagation();
            setSelected(prev => (prev && prev.id === n.id) ? null : n);
          });

        // Drop shadow rect
        ng.append('rect')
          .attr('x', 2).attr('y', 2)
          .attr('width', nodeW).attr('height', nodeH)
          .attr('rx', 9).attr('ry', 9)
          .attr('fill', 'rgba(0,0,0,0.08)');

        // Card rect — fill/stroke from design tokens
        ng.append('rect')
          .attr('width', nodeW).attr('height', nodeH)
          .attr('rx', 9).attr('ry', 9)
          .attr('fill', vis.fill === 'transparent' ? 'var(--paper)' : vis.fill)
          .attr('stroke', vis.stroke)
          .attr('stroke-width', vis.strokeW)
          .attr('stroke-dasharray', vis.strokeDash);

        // Node name text — truncated + centered
        const fullName = n.name || n.id;
        const maxChars = 26;
        const midY = nodeH / 2;

        if (fullName.length <= maxChars) {
          ng.append('text')
            .attr('class', 'dag-label')
            .attr('x', nodeW / 2).attr('y', midY + 4)
            .attr('text-anchor', 'middle')
            .attr('font-family', 'Host Grotesk, Inter, sans-serif')
            .attr('font-size', '11px').attr('font-weight', '600')
            .attr('fill', 'var(--ink)')
            .attr('pointer-events', 'none')
            .style('opacity', 0.85)
            .text(fullName);
        } else {
          // Two-line wrap at nearest space to midpoint
          const mid = Math.floor(fullName.length / 2);
          let split = fullName.lastIndexOf(' ', mid);
          if (split < 4) split = fullName.indexOf(' ', mid);
          if (split < 0) split = maxChars;
          const l1 = fullName.substring(0, split).trim();
          const l2 = fullName.substring(split).trim();
          ng.append('text').attr('class', 'dag-label')
            .attr('x', nodeW / 2).attr('y', midY - 4)
            .attr('text-anchor', 'middle')
            .attr('font-family', 'Host Grotesk, Inter, sans-serif')
            .attr('font-size', '10px').attr('font-weight', '600')
            .attr('fill', 'var(--ink)').attr('pointer-events', 'none')
            .style('opacity', 0.85)
            .text(l1.length > maxChars ? l1.substring(0, maxChars - 1) + '…' : l1);
          ng.append('text').attr('class', 'dag-label')
            .attr('x', nodeW / 2).attr('y', midY + 9)
            .attr('text-anchor', 'middle')
            .attr('font-family', 'Host Grotesk, Inter, sans-serif')
            .attr('font-size', '10px').attr('font-weight', '600')
            .attr('fill', 'var(--ink)').attr('pointer-events', 'none')
            .style('opacity', 0.85)
            .text(l2.length > maxChars ? l2.substring(0, maxChars - 1) + '…' : l2);
        }
      }

      // Background click → deselect
      svg.on('click', () => setSelected(null));

    } catch (err) {
      console.error('DAG build failed:', err);
      setDagError('DAG render failed: ' + (err && err.message ? err.message : String(err)));
    }
  }, [filteredGraphData]);

  // ── Highlight selected node in DAG ────────────────────────────────────────
  React.useEffect(() => {
    if (!dagSvgRef.current || !d3) return;
    d3.select(dagSvgRef.current).selectAll('g[transform]').each(function(d) {
      if (!d || !d.id) return;
      const rects = d3.select(this).selectAll('rect').nodes();
      // The card rect is the second rect (first is shadow)
      if (rects.length >= 2) {
        const cardRect = d3.select(rects[1]);
        if (selected && d.id === selected.id) {
          cardRect.attr('stroke-width', 2.5);
        } else {
          const vis = dagNodeVisual(d, {});
          cardRect.attr('stroke-width', vis.strokeW);
        }
      }
    });
  }, [selected]);

  // ── Highlight selected node ───────────────────────────────────────────────
  // Runs after each selection change. Reads the circle's existing 'r' from the DOM
  // (set during D3 build) so no degreeMap reference needed here.
  React.useEffect(() => {
    if (!svgRef.current || !d3) return;
    // Boost radius and stroke for selected; restore original for others.
    d3.select(svgRef.current).selectAll('.g-node').each(function(d) {
      const circle = d3.select(this).select('circle.node-circle');
      const origR = parseFloat(this.dataset.origR || circle.attr('r') || 10);
      if (!this.dataset.origR) this.dataset.origR = origR; // cache once
      if (selected && d.id === selected.id) {
        circle.attr('r', origR + 4).attr('stroke-width', 2.5);
      } else {
        circle.attr('r', origR);
        // Restore status-based stroke-width
        const col = nodeStatusColor(d, {});
        circle.attr('stroke-width', col.strokeW);
      }
    });
  }, [selected]);

  // ── Concept proven: listen for struggle-reel resolve events ──────────────
  // Fires even if GraphScreen is mounted after the event, because we attach
  // on mount and the CustomEvent is dispatched synchronously on the same window.
  // The in-memory status mutation means the node reads as 'proven' in the
  // side-panel without a full graph refetch.
  React.useEffect(() => {
    function handleConceptProven(e) {
      const ids = (e.detail && Array.isArray(e.detail.ids)) ? e.detail.ids : [];
      if (!ids.length || !d3) return;

      const idSet = new Set(ids);

      // Mutate node status in-memory so the side-panel reflects 'proven'
      // the next time the user selects one of these nodes.
      if (graphData) {
        const { nodes: allNodes } = parseGraphData(graphData);
        for (const n of allNodes) {
          if (idSet.has(n.id)) {
            // Bump mastery to indicate a small correct signal was recorded.
            n.mastery = Math.max(n.mastery || 0, 0.35);
            n.retrievability = Math.max(n.retrievability || 0, 0.75);
            n.status = 'proven';
          }
        }
      }

      // FDG animation: flash stroke + scale
      if (svgRef.current) {
        d3.select(svgRef.current)
          .selectAll('.g-node')
          .filter(d => d && idSet.has(d.id))
          .each(function(d) {
            // Mutate the live d3 datum so re-renders see the new state
            d.mastery = Math.max(d.mastery || 0, 0.35);
            d.retrievability = Math.max(d.retrievability || 0, 0.75);
            d.status = 'proven';

            const g = d3.select(this);
            const circle = g.select('circle.node-circle');
            const origR = parseFloat(this.dataset.origR || circle.attr('r') || 10);
            if (!this.dataset.origR) this.dataset.origR = origR;

            // Pulse: scale up with green stroke → ease back to normal
            circle
              .attr('stroke', 'var(--ok)')
              .attr('fill', 'rgb(97,197,84)')
              .attr('r', origR * 1.25)
              .attr('stroke-width', 3);

            setTimeout(() => {
              circle
                .transition()
                .duration(600)
                .attr('r', origR)
                .attr('stroke-width', 1.5);
            }, 240);
          });
      }

      // DAG animation: flash card rect stroke + fill
      if (dagSvgRef.current) {
        d3.select(dagSvgRef.current)
          .selectAll('g[transform]')
          .filter(d => d && idSet.has(d.id))
          .each(function(d) {
            // Mutate datum
            d.mastery = Math.max(d.mastery || 0, 0.35);
            d.retrievability = Math.max(d.retrievability || 0, 0.75);
            d.status = 'proven';

            const ng = d3.select(this);
            const rects = ng.selectAll('rect').nodes();
            if (rects.length < 2) return;
            const cardRect = d3.select(rects[1]);

            // Pulse green
            cardRect
              .attr('fill', 'rgb(97,197,84)')
              .attr('stroke', 'rgb(60,150,50)')
              .attr('stroke-width', 3);

            setTimeout(() => {
              cardRect
                .transition()
                .duration(600)
                .attr('stroke-width', 1.5);
            }, 240);
          });
      }
    }

    window.addEventListener('didactic:conceptProven', handleConceptProven);

    // Drain any ids queued before Graph was mounted (e.g. user resolved a
    // struggle on the Reel tab, THEN switched to Graph — the sync event had
    // already fired and been missed). Apply them once.
    const queued = window.__didacticProvenQueue || [];
    if (queued.length > 0) {
      handleConceptProven({ detail: { ids: queued.slice() } });
      window.__didacticProvenQueue = [];
    }

    return () => window.removeEventListener('didactic:conceptProven', handleConceptProven);
  }, [graphData]);   // re-bind if graphData changes so parseGraphData sees fresh nodes

  // ── Side panel ───────────────────────────────────────────────────────────
  const selChip = selected ? statusToChip(selected) : null;
  const masteryPct = selected ? Math.round((selected.mastery || 0) * 100) : 0;
  const retrievPct = selected && selected.retrievability != null
    ? Math.round(selected.retrievability * 100) : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <section className="graph">
      {/* Top bar */}
      <div className="graph-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 11,
            letterSpacing: '0.08em', color: 'var(--ink-3)', textTransform: 'uppercase',
          }}>
            Knowledge Graph
          </div>
          {/* View toggle — stacked pill segment control. */}
          <div className="graph-view-toggle">
            {[
              { id: "dag", label: "Path" },
              { id: "map", label: "Map"  },
            ].map(opt => (
              <button
                key={opt.id}
                className={"graph-view-btn" + (viewMode === opt.id ? " is-on" : "")}
                onClick={() => {
                  setViewMode(opt.id);
                  try { localStorage.setItem("didactic.graphView", opt.id); } catch (e) {}
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Course filter pills — CoursePills is a global Babel component (CoursePills.jsx) */}
        {planCourses.length > 0 && typeof CoursePills !== 'undefined' && (
          <CoursePills
            courses={planCourses}
            selected={courseSelected}
            onToggle={toggleCourse}
          />
        )}

        {/* Legend — top-right, 5 status rows */}
        <div className="graph-legend">
          {LEGEND_ITEMS.map(item => (
            <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <svg width="10" height="10" style={{ flexShrink: 0 }}>
                <circle cx="5" cy="5" r="4"
                  fill={item.fill}
                  stroke={item.stroke}
                  strokeWidth="1.5"
                  strokeDasharray={item.dash ? '3 2' : '0'}
                />
              </svg>
              <span style={{
                fontSize: 11, fontFamily: 'var(--font-sans)',
                color: 'var(--ink-2)', letterSpacing: '-0.03em',
              }}>
                {item.label}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* Body: left two-panel column + right sidebar */}
      <div className="graph-body">
        {/* Left column: renders ONE of DAG or FDG based on viewMode. */}
        <div className="graph-left-col">

          {/* ── PATH: DAG view ── */}
          <div className="graph-dag-wrap" style={{ display: viewMode === "dag" ? undefined : "none" }}>
            {loading && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-3)',
                background: 'var(--paper)', borderRadius: 12,
              }}>
                Loading…
              </div>
            )}
            {dagError && (
              <div className="dag-error-banner">{dagError}</div>
            )}
            <svg
              ref={dagSvgRef}
              className="graph-svg"
              style={{ width: '100%', height: '100%', display: 'block', background: 'var(--paper)' }}
            />
          </div>

          {/* ── MAP: Force-directed view ── */}
          <div className="graph-fdg-wrap" style={{ display: viewMode === "map" ? undefined : "none" }}>
            {/* Error overlay — always visible, never silent */}
            {error && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex',
                flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: 24,
                fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--tint-red)',
                background: 'rgba(255,244,232,0.97)',
                zIndex: 10, borderRadius: 12,
                border: '1px solid rgba(249,111,98,0.3)',
              }}>
                <span style={{ fontWeight: 700 }}>Graph error</span>
                <span style={{ color: 'var(--ink-2)', textAlign: 'center', maxWidth: 340 }}>{error}</span>
              </div>
            )}
            {/* D3 FDG target */}
            <svg
              ref={svgRef}
              className="graph-svg"
              style={{ width: '100%', height: '100%', display: 'block', background: 'var(--paper)' }}
            />
          </div>
        </div>

        {/* Right panel — 280px wide, node detail */}
        <aside className="graph-panel">
          {!selected ? (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 8,
            }}>
              <div style={{
                fontFamily: 'var(--font-sans)', fontSize: 13,
                color: 'var(--ink-3)', letterSpacing: '-0.03em', lineHeight: 1.5,
              }}>
                Click a node to see details
              </div>
              <div style={{
                fontFamily: 'var(--font-sans)', fontSize: 11,
                color: 'var(--ink-4)', letterSpacing: '-0.03em',
              }}>
                Drag to reposition · scroll to zoom
              </div>
            </div>
          ) : (
            <>
              {/* Breadcrumb */}
              <div className="panel-eyebrow">
                {selected.domain} · {selected.subject}
              </div>

              {/* Node name in serif 20px */}
              <div className="panel-title" style={{ fontSize: 20, wordBreak: 'break-word' }}>
                {selected.name}
              </div>

              {/* Status chip */}
              <div>
                <span className={`chip ${selChip}`}>
                  <span className="dot" />
                  {statusLabel(selChip)}
                </span>
              </div>

              {/* Mastery bar */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'var(--font-sans)', fontSize: 11,
                    color: 'var(--ink-3)', letterSpacing: '-0.03em',
                  }}>Mastery</span>
                  <span style={{
                    fontFamily: 'var(--font-sans)', fontSize: 12,
                    fontWeight: 700, color: 'var(--ink)', letterSpacing: '-0.03em',
                  }}>
                    {masteryPct}%
                  </span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: 'var(--paper-3)', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 3,
                    width: masteryPct + '%',
                    background: selChip === 'crisp' ? 'var(--ok)'
                      : selChip === 'decay' ? 'var(--tint-amber)'
                      : selChip === 'frontier' ? 'var(--probe)'
                      : 'var(--ink-4)',
                    transition: 'width 0.4s',
                  }} />
                </div>
              </div>

              {/* Stats grid: Retrievability / Stability / Difficulty / Type */}
              <div className="panel-stats">
                <div>
                  <div className="stat-label">Retrievability</div>
                  <div className="stat-value">
                    {retrievPct != null ? retrievPct + '%' : '—'}
                  </div>
                </div>
                <div>
                  <div className="stat-label">Stability</div>
                  <div className="stat-value">
                    {selected.stability != null ? selected.stability.toFixed(1) + 'd' : '—'}
                  </div>
                </div>
                <div>
                  <div className="stat-label">Difficulty</div>
                  <div className="stat-value">
                    {selected.difficulty != null
                      ? (selected.difficulty * 100).toFixed(0) + '%'
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="stat-label">Type</div>
                  <div className="stat-value" style={{ textTransform: 'capitalize' }}>
                    {selected.type}
                  </div>
                </div>
              </div>

              {/* Statement / definition (LaTeX auto-typeset). */}
              {selected.statement && (
                <TeX as="div" style={{
                  fontFamily: 'var(--font-sans)', fontSize: 12,
                  color: 'var(--ink-2)', letterSpacing: '-0.02em', lineHeight: 1.6,
                  borderTop: '1px solid var(--line-soft)', paddingTop: 10,
                }}>
                  {selected.statement}
                </TeX>
              )}

              <button className="btn-primary panel-btn" style={{ marginTop: 'auto' }}>
                Practice this derivation
              </button>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

